import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useOrganization } from "@/hooks/useOrganization";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Button } from "@/components/ui/button";
import { format, addMinutes, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { getLocalDayBounds, cn } from "@/lib/utils";
import { toast } from "sonner";
import { type PricingRule, type SpecialPriceDate } from "@/lib/pricing";
import { useBillingConfig } from "@/hooks/useBillingConfig";
import { Clock, AlertTriangle, Grid3X3, CheckSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import BookingGrid from "@/components/booking/BookingGrid";
import BookingQuickView from "@/components/booking/BookingQuickView";
import DateStrip from "@/components/shared/DateStrip";
import MultiSelectSummaryBar from "@/components/booking/MultiSelectSummaryBar";
import { useMultiSelect, type MultiSelectSlot } from "@/hooks/useMultiSelect";
import { calculateBookingPrice } from "@/lib/pricing";
import { useIsMobile } from "@/hooks/use-mobile";
import BookingWizard from "@/components/booking/BookingWizard";
import BookingSlotInfoDialog from "@/components/booking/BookingSlotInfoDialog";
import { Card, CardContent } from "@/components/ui/card";
import type { Tables } from "@/integrations/supabase/types";
import { useBookingState } from "@/hooks/useBookingState";
import { handleBookConfirmation, type HandleBookOpts } from "@/lib/bookingConfirmation";
// UPPDRAG-15: court sponsors fetched here so BookingGrid can render
// the per-court header stack without extra round-trips per row.
import { activeSponsorsSorted, type CourtSponsorRow } from "@/lib/courtSponsorHelpers";

/** Court row from DB */
type CourtRow = Tables<"courts">;

/** Booking row with joined courts */
interface BookingDayRow extends Tables<"bookings"> {
  courts: { name: string; hall_id: string } | null;
}

/** Waitlist row from DB */
type WaitlistRow = Tables<"waitlist">;

interface BookingSettings {
  gap_protection?: boolean;
  min_gap_minutes?: number;
}

export default function Book() {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courts: realCourts, hall, hallId, hallHours, isLoading: orgLoading } = useOrgHall();
  const { organization } = useOrganization();
  const { config: billingConfig } = useBillingConfig();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [bookingView, setBookingView] = useState<"timeline" | "quick">(isMobile ? "quick" : "timeline");

  // Waitlist offer from URL params
  const _waitlistId = searchParams.get("waitlist_id");

  // Fetch player's category for personalized pricing
  const { data: myCategory } = useQuery({
    queryKey: ["my-category", user?.id, organization?.id],
    queryFn: async () => {
      const { getPlayerCategory } = await import("@/lib/playerPricing");
      return getPlayerCategory(user!.id, organization!.id);
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const courts = useMemo(
    () => realCourts.filter((c: CourtRow) => c.status === "active"),
    [realCourts],
  );

  // Hall duration settings
  const minDuration = hall?.min_duration_minutes ?? 30;
  const maxDuration = hall?.max_duration_minutes ?? 120;
  const timeStep = hall?.time_step_minutes ?? 15;
  const bookingHorizon = hall?.booking_horizon_days ?? 14;
  const { isEnabled } = useFeatureFlags();
  const waitlistEnabled = isEnabled("waitlist");

  // Check for active waitlist offer for current user
  const { data: activeOffer } = useQuery({
    queryKey: ["waitlist-offer", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("waitlist")
        .select("*")
        .eq("user_id", user!.id)
        .eq("status", "offered")
        .gt("expires_at", new Date().toISOString())
        .order("offered_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
    refetchInterval: 10000,
  });

  // Check booking ban
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile-ban", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("booking_ban_until")
        .eq("id", user!.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  const isBanned = userProfile?.booking_ban_until && new Date(userProfile.booking_ban_until) > new Date();

  const { data: pricingRules = [] } = useQuery({
    queryKey: ["pricing-rules", hallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("hall_id", hallId!);
      if (error) throw error;
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  const { data: specialDates = [] } = useQuery({
    queryKey: ["special-price-dates", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("special_price_dates" as any)
        .select("*")
        .eq("organization_id", organization!.id);
      return (data as unknown as SpecialPriceDate[]) ?? [];
    },
    enabled: !!organization?.id,
  });

  // UPPDRAG-15: court sponsors. Single query for all sponsors of all
  // active courts in the hall; the result is grouped by court_id +
  // filtered to active-window entries below before passing to BookingGrid.
  const courtIds = useMemo(() => courts.map((c) => c.id), [courts]);
  const { data: rawCourtSponsors = [] } = useQuery({
    queryKey: ["court-sponsors", hallId, courtIds.join(",")],
    queryFn: async () => {
      if (courtIds.length === 0) return [];
      const { data, error } = await supabase
        .from("court_sponsors" as any)
        .select("id, court_id, sponsor_name, logo_url, click_url, active_from, active_to, display_order")
        .in("court_id", courtIds);
      if (error) throw error;
      return (data ?? []) as unknown as CourtSponsorRow[];
    },
    enabled: !!hallId && courtIds.length > 0,
    // Sponsor data changes rarely. Stale-time keeps the fetch off the
    // hot path of every booking-grid re-render.
    staleTime: 60_000,
  });

  // Group active+sorted sponsors by court_id for BookingGrid.
  const courtSponsorsByCourtId = useMemo(() => {
    const today = new Date();
    const map: Record<string, CourtSponsorRow[]> = {};
    for (const cid of courtIds) {
      const forCourt = rawCourtSponsors.filter((s) => s.court_id === cid);
      const active = activeSponsorsSorted(forCourt, today);
      if (active.length > 0) map[cid] = active;
    }
    return map;
  }, [courtIds, rawCourtSponsors]);

  const { data: bookings = [], refetch: refetchBookings } = useQuery({
    queryKey: ["bookings-day", hallId, selectedDate.toISOString()],
    queryFn: async () => {
      const { start, end } = getLocalDayBounds(selectedDate);
      const { data, error } = await supabase
        .from("bookings")
        .select("*, courts!inner(name, hall_id)")
        .eq("courts.hall_id", hallId!)
        .not("status", "in", "(cancelled)")
        .is("deleted_at", null)
        .gte("start_time", start)
        .lte("start_time", end);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  // Realtime subscription for bookings
  useEffect(() => {
    if (!hallId) return;
    const channel = supabase
      .channel("bookings-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, () => {
        qc.invalidateQueries({ queryKey: ["bookings-day", hallId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [hallId, qc]);

  // ── Recurring bookings (stående tider / subscriptions) ──
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const dayOfWeek = selectedDate.getDay();

  const { data: recurringBookings = [] } = useQuery({
    queryKey: ["recurring-bookings-day", organization?.id, dateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_bookings")
        .select("id, court_id, start_time, end_time, skipped_dates, interval_weeks, valid_from")
        .eq("organization_id", organization!.id)
        .eq("day_of_week", dayOfWeek)
        .lte("valid_from", dateStr)
        .gte("valid_until", dateStr)
        .eq("status", "active");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // ── Recurring blocks (named blocks like Byggpadel) ──
  const { data: recurringBlocks = [] } = useQuery({
    queryKey: ["recurring-blocks-day", organization?.id, dateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_blocks" as any)
        .select("id, court_ids, start_time, end_time, skipped_dates, interval_weeks, valid_from, valid_until, day_of_week")
        .eq("organization_id", organization!.id)
        .eq("day_of_week", dayOfWeek)
        .lte("valid_from", dateStr)
        .eq("status", "active");
      return (data ?? []).filter((b: any) => !b.valid_until || b.valid_until >= dateStr);
    },
    enabled: !!organization?.id,
  });

  // ── TASK-75 PR-D Layer 1 — Placed league_matches (no booking_id yet) ──
  // These slots are reserved for league play but haven't been converted
  // into booking rows. Layer 4 (DB trigger) blocks the INSERT regardless;
  // this query exists purely so the customer UI can render them as
  // visually blocked, preventing the wasted click-through.
  const { data: leagueMatches = [] } = useQuery({
    queryKey: ["league-matches-bookable", hallId, dateStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_matches")
        .select(
          "id, court_id, scheduled_date, scheduled_time, status, leagues!inner(id, match_duration_minutes, hall_id)",
        )
        .eq("scheduled_date", dateStr)
        .not("scheduled_time", "is", null)
        .neq("status", "cancelled")
        .eq("leagues.hall_id", hallId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  // ── Merge all sources into unified booking list ──
  const allBookings = useMemo(() => {
    const synth: any[] = [];

    for (const rb of recurringBookings as any[]) {
      if (rb.skipped_dates?.includes(dateStr)) continue;
      if (rb.interval_weeks && rb.interval_weeks > 1 && rb.valid_from) {
        const diffMs = new Date(dateStr).getTime() - new Date(rb.valid_from).getTime();
        const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
        if (diffWeeks % rb.interval_weeks !== 0) continue;
      }
      synth.push({
        id: `rb-${rb.id}`,
        court_id: rb.court_id,
        start_time: `${dateStr}T${rb.start_time}`,
        end_time: `${dateStr}T${rb.end_time}`,
        status: "blocked",
        user_id: "recurring",
        is_open_match: false,
        courts: null,
      });
    }

    for (const block of recurringBlocks as any[]) {
      if (block.skipped_dates?.includes(dateStr)) continue;
      for (const courtId of block.court_ids ?? []) {
        synth.push({
          id: `block-${block.id}-${courtId}`,
          court_id: courtId,
          start_time: `${dateStr}T${block.start_time}`,
          end_time: `${dateStr}T${block.end_time}`,
          status: "blocked",
          user_id: "recurring",
          is_open_match: false,
          courts: null,
        });
      }
    }

    return [...bookings, ...synth];
  }, [bookings, recurringBookings, recurringBlocks, dateStr]);

  // Booking state hook (slots, add/remove court, max minutes, etc.)
  const {
    selectedSlots,
    setSelectedSlots,
    confirmSlot,
    setConfirmSlot,
    handleSlotClick,
    handleAddCourt,
    handleRemoveCourt,
    maxAvailableMinutes,
    availableCourtsForTime,
  } = useBookingState({
    bookings: allBookings as BookingDayRow[],
    courts,
    hallHours: hallHours ?? null,
    selectedDate,
    minDuration,
    maxDuration,
  });

  // Multi-select mode — determine max slots based on member status
  const isMember = myCategory?.slug === "member" || myCategory?.slug === "medlem";
  const effectiveMaxSlots = isMember
    ? (organization?.member_max_slots_per_booking ?? 8)
    : (organization?.max_slots_per_booking ?? 4);
  const multiSelect = useMultiSelect(selectedDate, effectiveMaxSlots);

  const buildMultiSelectSlot = (courtId: string, startTime: Date): MultiSelectSlot | null => {
    const court = courts.find((c: CourtRow) => c.id === courtId);
    if (!court || !hallId) return null;
    const endTime = addMinutes(startTime, minDuration);
    const cat = myCategory?.slug ?? "standard";
    const priceInfo = calculateBookingPrice(pricingRules, hallId, startTime, minDuration, cat, specialDates);
    return {
      courtId,
      courtName: court.name,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      price: priceInfo.total,
    };
  };

  const handleMultiSelectToggle = (courtId: string, startTime: Date) => {
    const slot = buildMultiSelectSlot(courtId, startTime);
    if (!slot) return;
    // Check if we're already at limit before toggling (only relevant for adding)
    const isAlreadySelected = multiSelect.isSlotSelected(courtId, startTime.toISOString());
    if (!isAlreadySelected && multiSelect.isAtLimit) {
      toast.info(t('maxSlotsPerBooking', { count: effectiveMaxSlots }));
      return;
    }
    multiSelect.toggleSlot(slot);
  };

  const handleMultiSelectDragStart = (courtId: string, startTime: Date) => {
    multiSelect.startDrag(courtId, startTime.toISOString());
  };

  const handleMultiSelectDragContinue = (courtId: string, startTime: Date) => {
    if (!multiSelect.isDragging) return;
    const iso = startTime.toISOString();
    const alreadySelected = multiSelect.isSlotSelected(courtId, iso);
    if (multiSelect.dragMode === "select" && !alreadySelected) {
      const slot = buildMultiSelectSlot(courtId, startTime);
      if (slot) multiSelect.selectSlot(slot);
    } else if (multiSelect.dragMode === "deselect" && alreadySelected) {
      multiSelect.deselectSlot(courtId, iso);
    }
  };

  const handleMultiSelectDragEnd = () => {
    multiSelect.endDrag();
  };

  const handleMultiBook = () => {
    if (multiSelect.selectedSlots.length === 0) return;
    // F2: set ALL selected slots. confirmSlot derives from selectedSlots[0]
    // (useBookingState), which opens the wizard. The old setConfirmSlot() call
    // here COLLAPSED the array back to [first] — the reason "Boka N tider" never
    // booked more than one slot (and why G-002's per-slot end_time corruption
    // stayed latent: multi-slot never reached the insert loop).
    const slots = multiSelect.selectedSlots.map((s) => ({
      courtId: s.courtId,
      startTime: new Date(s.startTime),
    }));
    setSelectedSlots(slots);
  };

  // Wrap date change to warn about clearing multi-select
  const handleDateChange = (newDate: Date) => {
    if (multiSelect.enabled && multiSelect.selectedSlots.length > 0 && newDate.getTime() !== selectedDate.getTime()) {
      const confirmed = window.confirm(
        t('multiSelectDateChangeWarning', { count: multiSelect.selectedSlots.length })
      );
      if (!confirmed) return;
      multiSelect.clearSelection();
    }
    setSelectedDate(newDate);
    setConfirmSlot(null);
  };

  // Auto-select the offered slot
  useEffect(() => {
    if (activeOffer && !confirmSlot) {
      const offerDate = new Date(activeOffer.date + "T00:00:00");
      setSelectedDate(startOfDay(offerDate));
    }
  }, [activeOffer]);

  // PR-AO — Booked-slot info popup state.
  //
  // Old UX: clicking a booked slot in BookingGrid auto-inserted the user
  // into the waitlist. Now we open this dialog showing slot details +
  // queue count, and the user must explicitly press "Ställ mig på kö".
  const [queueDialogState, setQueueDialogState] = useState<{
    courtId: string;
    courtName: string;
    startTime: Date;
    endTime: Date;
    queueCount: number;
  } | null>(null);
  const [isJoiningQueue, setIsJoiningQueue] = useState(false);

  // Click on a booked slot: prefetch queue count, then open popup.
  // Replaces the old auto-insert flow (which Book.tsx historically did
  // inline). Insert itself happens in performJoinWaitlist below, only
  // after explicit user confirmation.
  const handleJoinWaitlist = async (courtId: string, startTime: Date, endTime: Date) => {
    if (!user || !organization) return;

    const date = format(startTime, "yyyy-MM-dd");
    const start = format(startTime, "HH:mm:ss");

    // Already-in-queue check stays at click-time so we never open the
    // popup when the user is already queued (no actionable CTA).
    const { data: existing } = await supabase
      .from("waitlist")
      .select("id")
      .eq("user_id", user.id)
      .eq("court_id", courtId)
      .eq("date", date)
      .eq("start_time", start)
      .in("status", ["waiting", "offered"])
      .limit(1);

    if (existing && existing.length > 0) {
      toast.info(t('alreadyInQueue'));
      return;
    }

    // Fetch queue count for "X personer i kö" copy in the popup.
    // RLS-safe: 'Org members can read waitlist' policy lets count run for
    // any org member without leaking row identities (HEAD count only).
    const { count } = await supabase
      .from("waitlist")
      .select("id", { count: "exact", head: true })
      .eq("court_id", courtId)
      .eq("date", date)
      .eq("start_time", start)
      .eq("status", "waiting");

    const court = realCourts.find((c: CourtRow) => c.id === courtId);
    setQueueDialogState({
      courtId,
      courtName: court?.name ?? t('court', { defaultValue: 'Bana' }),
      startTime,
      endTime,
      queueCount: count ?? 0,
    });
  };

  // Triggered from the popup's "Ställ mig på kö" button.
  const performJoinWaitlist = async () => {
    if (!queueDialogState || !user || !organization) return;
    setIsJoiningQueue(true);
    const { courtId, startTime, endTime, queueCount } = queueDialogState;

    const date = format(startTime, "yyyy-MM-dd");
    const start = format(startTime, "HH:mm:ss");
    const end = format(endTime, "HH:mm:ss");

    const { error } = await supabase.from("waitlist").insert({
      user_id: user.id,
      organization_id: organization.id,
      court_id: courtId,
      date,
      start_time: start,
      end_time: end,
      // Position = current visible count + 1. Tiny race window vs other
      // joiners between the count fetch and this INSERT — acceptable since
      // position is advisory (waitlist offer-flow re-orders by created_at).
      position: queueCount + 1,
      status: "waiting",
    });

    setIsJoiningQueue(false);

    if (error) {
      toast.error(t('couldNotJoinQueue'));
      return;
    }

    toast.success(t('addedToQueue'));
    setQueueDialogState(null);
  };

  // Decline waitlist offer
  const handleDeclineOffer = async () => {
    if (!activeOffer) return;
    await supabase.from("waitlist").update({ status: "expired" }).eq("id", activeOffer.id);
    qc.invalidateQueries({ queryKey: ["waitlist-offer"] });
    toast.info(t('offerDeclined'));
  };

  const handleBook = async (opts: HandleBookOpts) => {
    if (!confirmSlot || !user || !hallHours || !hallId || !organization) return;

    const result = await handleBookConfirmation(
      {
        supabase,
        user: { id: user.id, email: user.email, user_metadata: user.user_metadata as Record<string, unknown> },
        confirmSlot,
        selectedSlots,
        courts,
        organization: {
        id: organization.id,
        name: organization.name,
        settings: organization.settings as Record<string, unknown> | null,
        max_concurrent_bookings: organization.max_concurrent_bookings,
        member_max_concurrent_bookings: organization.member_max_concurrent_bookings,
      },
        hallId,
        hallHours,
        bookings: allBookings as Tables<"bookings">[],
        pricingRules,
        specialDates,
        billingConfig: billingConfig as Record<string, unknown> | null,
        activeOffer: activeOffer ?? null,
        isBanned: !!isBanned,
        banUntil: userProfile?.booking_ban_until ?? null,
        baseUrl: window.location.origin,
        isMember,
      },
      opts,
    );

    // Process toasts
    for (const toastItem of result.toasts) {
      if (typeof toastItem.message === "object" && toastItem.message.jsx) {
        toast.success(
          <div>
            <p className="font-semibold">{t('bookingConfirmed')} ✅</p>
            <p className="text-sm">{t('pinCode')}: <span className="font-mono font-bold">{toastItem.message.pinCode}</span></p>
          </div>,
        );
      } else if (toastItem.type === "error") {
        toast.error(toastItem.message as string);
      } else if (toastItem.type === "info") {
        toast.info(toastItem.message as string);
      } else {
        toast.success(toastItem.message as string);
      }
    }

    // If payment failed, throw to prevent wizard from showing success
    if (!result.success) {
      throw new Error("Payment failed");
    }

    if (result.redirectUrl) {
      window.location.href = result.redirectUrl;
      return;
    }

    if (result.shouldInvalidateWaitlist) {
      qc.invalidateQueries({ queryKey: ["waitlist-offer"] });
    }

    if (result.shouldRefetchBookings) {
      refetchBookings();
    }

    // Spår 1b: V2 (open_matches_v2 + create-open-match edge-fn) rivet enligt
    // Scenario (a). "isActivity"-flowet skapar bara bokningen; den
    // adminstyrda activity-skapningen sker via /create-match (V1) eller
    // /manager/schedule (admin). Booking_id ger fortfarande "is_open_match"
    // marker på raden om opts.isActivity=true (görs i bookingConfirmation).

    // G-005: do NOT null confirmSlot on booking success — that unmounted the
    // wizard before it could reach its "confirmation" step (BookingWizard
    // goToStep("confirmation") after onConfirm). StepConfirmation now renders;
    // confirmSlot/selectedSlots clear via onOpenChange when the user clicks
    // "Klar" (result.shouldClearSlot is left unread by design).
    void result.shouldClearSlot;
    // G-105: lastBookingId removed (dead in BookingWizard since T1).
  };

  // ── ALL hooks MUST be called BEFORE any early return (Rules of Hooks) ──
  const selectedCourt = confirmSlot ? courts.find((c: CourtRow) => c.id === confirmSlot.courtId) : null;

  const modalCourts = useMemo(() =>
    selectedSlots.map((s) => {
      const c = courts.find((ct: CourtRow) => ct.id === s.courtId);
      return { courtId: s.courtId, courtName: c?.name ?? "Bana", courtType: c?.court_type, maxPlayers: c?.max_players ?? 4 };
    }),
    [selectedSlots, courts],
  );

  const modalAvailableCourts = useMemo(() =>
    availableCourtsForTime.map((c: CourtRow) => ({ id: c.id, name: c.name, type: c.court_type })),
    [availableCourtsForTime],
  );

  // Handle clicking on a booked slot (own booking or activity).
  // G-026/072: an activity cell (activity_id set) routes to its detail page;
  // a plain booking falls back to /bookings.
  const handleBookingClick = useCallback(
    (booking: { id: string; is_open_match?: boolean; activity_id?: string | null }) => {
      if (booking.activity_id) {
        navigate(`/activities/${booking.activity_id}`);
      } else {
        navigate(`/bookings`);
      }
    },
    [navigate]
  );

  const bookingSettingsGap = (organization?.settings?.booking ?? {}) as BookingSettings;
  const gapEnabled = bookingSettingsGap.gap_protection ?? false;
  const gapMinutes = bookingSettingsGap.min_gap_minutes ?? 60;
  const gapProtectionMemo = useMemo(() => {
    if (!gapEnabled) return undefined;
    return { enabled: true, minGapMinutes: gapMinutes, defaultDuration: minDuration };
  }, [gapEnabled, gapMinutes, minDuration]);

  // ── Early returns AFTER all hooks ──
  if (!hallHours && orgLoading) {
    return (
      <div className="space-y-4 pb-20">
        <div className="gradient-hero rounded-2xl p-5 -mx-1">
          <h1 className="text-2xl font-bold">{t('book')}</h1>
          <p className="text-sm text-muted-foreground">{t('loadingHallHours')}</p>
        </div>
      </div>
    );
  }

  if (!hallHours) return null;

  return (
    <div className="space-y-4 pb-20 lg:pb-6">
      <h1 className="text-lg font-semibold">{t('book')}</h1>

      {/* Booking ban banner */}
      {isBanned && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
            <div>
              <p className="font-semibold text-sm text-destructive">{t('bookingBanned')}</p>
              <p className="text-xs text-muted-foreground">
                {t('bookingBannedUntil', { date: new Date(userProfile!.booking_ban_until!).toLocaleDateString("sv-SE") })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Waitlist offer banner */}
      {activeOffer && (
        <WaitlistOfferBanner
          offer={activeOffer}
          courts={courts}
          onBook={() => {
            const startTime = new Date(activeOffer.date + "T" + activeOffer.start_time);
            setConfirmSlot({ courtId: activeOffer.court_id!, startTime });
          }}
          onDecline={handleDeclineOffer}
        />
      )}

      <div className="space-y-4">
          {/* View toggle — mobile + desktop */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              <button
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                  bookingView === "quick" ? "bg-background shadow-sm" : "text-muted-foreground",
                )}
                onClick={() => setBookingView("quick")}
              >
                <Grid3X3 className="h-3.5 w-3.5 inline mr-1" />
                {t('quickBook')}
              </button>
              <button
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                  bookingView === "timeline" ? "bg-background shadow-sm" : "text-muted-foreground",
                )}
                onClick={() => setBookingView("timeline")}
              >
                <Clock className="h-3.5 w-3.5 inline mr-1" />
                {t('timeline')}
              </button>
            </div>
            {bookingView === "timeline" && (
              <Button
                variant={multiSelect.enabled ? "default" : "outline"}
                size="sm"
                className="gap-1.5"
                onClick={() => multiSelect.setEnabled(!multiSelect.enabled)}
              >
                <CheckSquare className="h-3.5 w-3.5" />
                {multiSelect.enabled ? t('cancelSelection') : t('selectMultiple')}
              </Button>
            )}
          </div>

          {/* Quick view or Timeline based on toggle */}
          {bookingView === "quick" ? (
            <BookingQuickView
              courts={courts}
              bookings={allBookings}
              hallHours={hallHours}
              selectedDate={selectedDate}
              onDateChange={(d) => { setSelectedDate(d); setConfirmSlot(null); }}
              bookingHorizon={bookingHorizon}
              timeStepMinutes={timeStep}
              onSelectSlot={(courtId, startTime) => setConfirmSlot({ courtId, startTime })}
              pricingRules={pricingRules}
              specialDates={specialDates}
              hallId={hallId}
              defaultDuration={minDuration}
              maxDuration={maxDuration}
              categorySlug={myCategory?.slug}
              courtAssignmentMode={(hall as any)?.court_assignment_mode ?? "manual"}
              onBookMultiple={(courtIds, startTime, _duration) => {
                // F2: preserve all N courts; confirmSlot derives from
                // selectedSlots[0]. (setConfirmSlot would collapse to [first].)
                // G-108 (duration forward) needs a BookingWizard initial-duration
                // prop — flagged, T1-owned file.
                const slots = courtIds.map((cId) => ({
                  courtId: cId,
                  startTime,
                }));
                setSelectedSlots(slots);
              }}
            />
          ) : (
            <>
              {/* Date selector — horizontal scroll strip */}
              <DateStrip
                selectedDate={selectedDate}
                onSelect={(d) => handleDateChange(d ?? startOfDay(new Date()))}
                showAllButton={false}
                days={bookingHorizon}
              />

              {/* Timeline grid */}
              <BookingGrid
                courts={courts}
                bookings={allBookings}
                hallHours={hallHours}
                selectedDate={selectedDate}
                userId={user?.id}
                timeStepMinutes={timeStep}
                onSlotClick={handleSlotClick}
                // 009/GO-TILLÄGG p2 (L21): den befintliga "Bokad · Köa?"-vägen var
                // HELT ogatad — bara adminytan (/manager/waitlist) läste flaggan.
                // En hall med waitlist AV kunde alltså samla köposter som ingen
                // admin hade en sida att se dem på. BookingGrid renderar bara
                // "Köa?" när propen finns, så gaten sitter här.
                onJoinWaitlist={waitlistEnabled ? handleJoinWaitlist : undefined}
                onBookingClick={handleBookingClick}
                pricingRules={pricingRules}
                specialDates={specialDates}
                hallId={hallId}
                categorySlug={myCategory?.slug}
                selectedSlots={selectedSlots}
                gapProtection={gapProtectionMemo}
                multiSelectEnabled={multiSelect.enabled}
                multiSelectedSlots={multiSelect.selectedSlots}
                onMultiSelectToggle={handleMultiSelectToggle}
                onMultiSelectDragStart={handleMultiSelectDragStart}
                onMultiSelectDragContinue={handleMultiSelectDragContinue}
                onMultiSelectDragEnd={handleMultiSelectDragEnd}
                multiSelectIsDragging={multiSelect.isDragging}
                multiSelectIsAtLimit={multiSelect.isAtLimit}
                /* UPPDRAG-15: pre-grouped + active-filtered sponsors */
                courtSponsors={courtSponsorsByCourtId}
                /* TASK-75 PR-D: placed league matches as visually-blocked slots */
                leagueMatches={leagueMatches}
              />

              {/* Legend */}
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-court-free/40 ring-1 ring-court-free/30" /> {t('legendFree')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-court-booked/40 ring-1 ring-court-booked/30" /> {t('legendBooked')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-court-mine/40 ring-1 ring-court-mine/30" /> {t('legendMine')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-court-partial/40 ring-1 ring-court-partial/30" /> {t('openMatch')}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                {t('clickTimeToBook')}
              </p>
            </>
          )}
      </div>

      {/* Multi-select summary bar */}
      {multiSelect.enabled && multiSelect.selectedSlots.length > 0 && (
        <MultiSelectSummaryBar
          slots={multiSelect.selectedSlots}
          totalPrice={multiSelect.totalPrice}
          onClear={multiSelect.clearSelection}
          onRemoveSlot={multiSelect.removeSlot}
          onBook={handleMultiBook}
          maxSlots={effectiveMaxSlots}
        />
      )}

      {/* Confirm dialog */}
      {confirmSlot && hallId && organization?.id && user?.id && (
        <BookingWizard
          open={!!confirmSlot}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedSlots([]);
              // Clear multi-select after booking wizard closes
              if (multiSelect.enabled) {
                multiSelect.clearSelection();
                multiSelect.setEnabled(false);
              }
            }
          }}
          courtName={selectedCourt?.name ?? "Bana"}
          courtId={confirmSlot.courtId}
          courtType={selectedCourt?.court_type ?? "double"}
          startTime={confirmSlot.startTime}
          hallId={hallId}
          organizationId={organization.id}
          pricingRules={pricingRules}
          userId={user.id}
          minDuration={minDuration}
          maxDuration={maxDuration}
          timeStep={timeStep}
          maxAvailableMinutes={maxAvailableMinutes}
          onConfirm={handleBook}
          specialDates={specialDates}
          courts={modalCourts}
          availableCourts={modalAvailableCourts}
          onAddCourt={handleAddCourt}
          onRemoveCourt={handleRemoveCourt}
          bookerCategorySlug={myCategory?.slug ?? "standard"}
          multiSelectSlots={multiSelect.enabled ? multiSelect.selectedSlots : undefined}
          onRemoveMultiSlot={multiSelect.enabled ? multiSelect.removeSlot : undefined}
        />
      )}

      {/* PR-AO — Booked-slot info popup. Replaces previous auto-add-to-queue
          UX. handleJoinWaitlist (above) sets queueDialogState; this dialog
          renders the slot details + queue count and asks for explicit
          confirmation before performJoinWaitlist commits the INSERT. */}
      {queueDialogState && (
        <BookingSlotInfoDialog
          open={!!queueDialogState}
          onOpenChange={(open) => { if (!open) setQueueDialogState(null); }}
          courtName={queueDialogState.courtName}
          startTime={queueDialogState.startTime}
          endTime={queueDialogState.endTime}
          queueCount={queueDialogState.queueCount}
          onConfirm={performJoinWaitlist}
          isPending={isJoiningQueue}
        />
      )}
    </div>
  );
}

// Waitlist offer banner component
function WaitlistOfferBanner({ offer, courts, onBook, onDecline }: {
  offer: WaitlistRow;
  courts: CourtRow[];
  onBook: () => void;
  onDecline: () => void;
}) {
  const { t } = useTranslation('booking');
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const update = () => {
      const expiresAt = new Date(offer.expires_at).getTime();
      const now = Date.now();
      const diff = expiresAt - now;
      if (diff <= 0) {
        setTimeLeft("0:00");
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${String(secs).padStart(2, "0")}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [offer.expires_at]);

  const courtName = courts.find((c: CourtRow) => c.id === offer.court_id)?.name ?? "Bana";

  return (
    <Card className="border-primary bg-primary/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 space-y-2">
            <p className="font-semibold text-sm">
              {t('timeReservedForYou', { time: timeLeft })}
            </p>
            <p className="text-xs text-muted-foreground">
              {courtName} · {format(new Date(offer.date + "T00:00:00"), "d MMM", { locale: sv })} · {offer.start_time?.slice(0, 5)}–{offer.end_time?.slice(0, 5)}
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={onBook}>{t('bookNow')}</Button>
              <Button size="sm" variant="outline" onClick={onDecline}>{t('decline')}</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
